import { Lexer, type Token, type Tokens } from 'marked'
import { createMemo, For, type JSX, Match, Show, Switch } from 'solid-js'
import { cn } from '../lib/cn.ts'

/**
 * Assistant replies arrive as Markdown. Tokens are rendered as Solid elements, never as HTML, so
 * text from the model cannot inject markup. Raw HTML in a reply is shown as plain text.
 */
const lex = (text: string): Token[] => {
  try {
    return new Lexer({ gfm: true, breaks: true }).lex(text)
  } catch {
    return [{ type: 'paragraph', raw: text, text, tokens: [{ type: 'text', raw: text, text }] }]
  }
}

const safeHref = (href: string) => (/^(https?:|mailto:|tel:|\/)/i.test(href) ? href : undefined)

const Inline = (props: { tokens?: Token[]; fallback?: string }): JSX.Element => (
  <Show when={props.tokens?.length} fallback={props.fallback}>
    <For each={props.tokens}>{(token) => <InlineToken token={token} />}</For>
  </Show>
)

const InlineToken = (props: { token: Token }): JSX.Element => {
  const token = () => props.token as Tokens.Generic
  return (
    <Switch fallback={token().raw}>
      <Match when={props.token.type === 'text' || props.token.type === 'escape'}>
        <Inline tokens={token().tokens} fallback={token().text} />
      </Match>
      <Match when={props.token.type === 'strong'}>
        <strong class="font-semibold text-text">
          <Inline tokens={token().tokens} fallback={token().text} />
        </strong>
      </Match>
      <Match when={props.token.type === 'em'}>
        <em>
          <Inline tokens={token().tokens} fallback={token().text} />
        </em>
      </Match>
      <Match when={props.token.type === 'del'}>
        <del>
          <Inline tokens={token().tokens} fallback={token().text} />
        </del>
      </Match>
      <Match when={props.token.type === 'codespan'}>
        <code class="rounded-[0.375rem] bg-surface-sunken px-1 py-0.5 font-mono text-[0.92em]">
          {token().text}
        </code>
      </Match>
      <Match when={props.token.type === 'br'}>
        <br />
      </Match>
      <Match when={props.token.type === 'link'}>
        <Show
          when={safeHref(String(token().href))}
          fallback={<Inline tokens={token().tokens} fallback={token().text} />}
        >
          {(href) => (
            <a
              href={href()}
              target="_blank"
              rel="noopener noreferrer"
              class="text-accent underline-offset-2 hover:underline"
            >
              <Inline tokens={token().tokens} fallback={token().text} />
            </a>
          )}
        </Show>
      </Match>
    </Switch>
  )
}

const ListBlock = (props: { token: Tokens.List }) => {
  const items = () => (
    <For each={props.token.items}>
      {(item) => (
        <li class="pl-1">
          <Blocks tokens={item.tokens} tight />
        </li>
      )}
    </For>
  )
  return (
    <Show
      when={props.token.ordered}
      fallback={<ul class="grid list-disc gap-1 pl-5 marker:text-text-subtle">{items()}</ul>}
    >
      <ol
        start={typeof props.token.start === 'number' ? props.token.start : undefined}
        class="grid list-decimal gap-1 pl-5 marker:text-text-subtle"
      >
        {items()}
      </ol>
    </Show>
  )
}

const TableBlock = (props: { token: Tokens.Table }) => {
  const align = (value: Tokens.TableCell['align']) =>
    value === 'right' ? 'text-right' : value === 'center' ? 'text-center' : 'text-left'
  return (
    <div class="overflow-x-auto rounded-[0.75rem] shadow-[0_0_0_1px_var(--color-border)]">
      <table class="w-full border-collapse text-body">
        <thead class="bg-surface-muted">
          <tr>
            <For each={props.token.header}>
              {(cell) => (
                <th
                  class={cn(
                    'px-3 py-2 align-bottom text-caption font-medium whitespace-nowrap text-text-muted',
                    align(cell.align),
                  )}
                >
                  <Inline tokens={cell.tokens} fallback={cell.text} />
                </th>
              )}
            </For>
          </tr>
        </thead>
        <tbody>
          <For each={props.token.rows}>
            {(row) => (
              <tr class="border-t border-border">
                <For each={row}>
                  {(cell) => (
                    <td class={cn('px-3 py-2 align-top tabular-nums', align(cell.align))}>
                      <Inline tokens={cell.tokens} fallback={cell.text} />
                    </td>
                  )}
                </For>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </div>
  )
}

const Block = (props: { token: Token; tight?: boolean }): JSX.Element => {
  const token = () => props.token as Tokens.Generic
  return (
    <Switch fallback={<p class="whitespace-pre-wrap">{token().raw}</p>}>
      <Match when={props.token.type === 'space'}>{null}</Match>
      <Match when={props.token.type === 'paragraph'}>
        <p>
          <Inline tokens={token().tokens} fallback={token().text} />
        </p>
      </Match>
      <Match when={props.token.type === 'text'}>
        <Show
          when={props.tight}
          fallback={<p>{<Inline tokens={token().tokens} fallback={token().text} />}</p>}
        >
          <span>
            <Inline tokens={token().tokens} fallback={token().text} />
          </span>
        </Show>
      </Match>
      <Match when={props.token.type === 'heading'}>
        <p
          class={cn(
            'font-semibold text-text',
            Number(token().depth) <= 2 ? 'text-[1.05em]' : 'text-body',
          )}
        >
          <Inline tokens={token().tokens} fallback={token().text} />
        </p>
      </Match>
      <Match when={props.token.type === 'list'}>
        <ListBlock token={props.token as Tokens.List} />
      </Match>
      <Match when={props.token.type === 'table'}>
        <TableBlock token={props.token as Tokens.Table} />
      </Match>
      <Match when={props.token.type === 'code'}>
        <pre class="overflow-x-auto rounded-[0.75rem] bg-surface-sunken px-3 py-2 font-mono text-caption leading-relaxed">
          <code>{token().text}</code>
        </pre>
      </Match>
      <Match when={props.token.type === 'blockquote'}>
        <blockquote class="grid gap-2 border-l-2 border-border pl-3 text-text-muted">
          <Blocks tokens={token().tokens} />
        </blockquote>
      </Match>
      <Match when={props.token.type === 'hr'}>
        <hr class="border-border" />
      </Match>
    </Switch>
  )
}

const Blocks = (props: { tokens?: Token[]; tight?: boolean }) => (
  <For each={props.tokens ?? []}>{(token) => <Block token={token} tight={props.tight} />}</For>
)

export const AiMarkdown = (props: { text: string; class?: string }) => {
  const tokens = createMemo(() => lex(props.text))
  return (
    <div class={cn('grid gap-2 break-words text-body text-text', props.class)}>
      <Blocks tokens={tokens()} />
    </div>
  )
}
